import { Link } from 'react-router-dom'
import { styled, CSS, css } from 'theme'
import { Flex } from './Flex'
import { ReactNode } from 'react'
import { SvgWrap } from 'components/svg'
import {
  anchorStyles,
  anchorStylesExtend,
  h1Styles,
  h1StylesExtend,
  h2Styles,
  h2StylesExtend,
  h3Styles,
  h3StylesExtend,
  h4Styles,
  h5Styles,
  h6Styles,
  helperTextStyles,
  labelStyles,
  paraStyles,
  paraStylesExtend,
  tooltipStyles,
} from './styleText'
import { TextDefaultVariants, TextVariants } from './textVariants'
import { AvatarSizeProps } from 'components/avatar'

export const TextBase = styled('span', {
  margin: 0,
  color: '$blackBase',
  letterSpacing: 'normal',
  variants: {
    color: TextVariants.color,
  },
})

export const TextDefault = styled(TextBase, {
  variants: TextVariants,
  defaultVariants: TextDefaultVariants,
})

// Styled components for various text types
const H1 = styled(TextBase, h1Styles)
const H1Extended = styled(TextBase, h1StylesExtend)
const H2 = styled(TextBase, h2Styles)
const H2Extended = styled(TextBase, h2StylesExtend)
const H3 = styled(TextBase, h3Styles)
const H3Extended = styled(TextBase, h3StylesExtend)
const H4 = styled(TextBase, h4Styles)
const H5 = styled(TextBase, h5Styles)
const H6 = styled(TextBase, h6Styles)
const LABEL = styled(TextBase, labelStyles)
const PARA = styled(TextBase, paraStyles)
const PARAExtended = styled(TextBase, paraStylesExtend)
export const ANCHOR = styled(TextBase, Link, anchorStyles)
const ANCHORExtended = styled(TextBase, Link, anchorStylesExtend)
const TOOLTIP = styled(TextBase, tooltipStyles)
const HELPERTEXT = styled(TextBase, helperTextStyles)

// Type definitions for text properties
export type TextColorProps =
  | 'primary'
  | 'secondary'
  | 'warning'
  | 'error'
  | 'success'
  | 'gray'
  | 'white'
  | 'black'
  | 'disabled'

export type TextAsProps =
  | 'h1'
  | 'h1Extend'
  | 'h2'
  | 'h2Extend'
  | 'h3'
  | 'h3Extend'
  | 'h4'
  | 'h5'
  | 'h6'
  | 'p'
  | 'pExtend'
  | 'a'
  | 'aExtend'
  | 'label'
  | 'tooltip'
  | 'helperText'
  | 'span'

interface TextComponentProps {
  dangerouslySetInnerHTML?: { __html: string }
  className?: string
  css?: CSS
  styleBox?: CSS
  as?: TextAsProps
  children?: ReactNode
  color?: keyof typeof TextVariants.color
  shrink?: boolean
  htmlFor?: string
  icon?: {
    size?: AvatarSizeProps
    Before?: ReactNode
    After?: ReactNode
    gap?: CSS
  }
  href?: string
  target?: string
  onClick?: () => void
}

// Function to generate text elements based on the "as" prop
const generateTextElements = (
  as: TextComponentProps['as'],
  children: React.ReactNode,
  rest: Omit<TextComponentProps, 'as' | 'children'>
) => {
  switch (as) {
    case 'h1':
      return (
        <H1 as="h1" {...rest}>
          {children}
        </H1>
      )
    case 'h1Extend':
      return (
        <H1Extended as="h1" {...rest}>
          {children}
        </H1Extended>
      )
    case 'h2':
      return (
        <H2 as="h2" {...rest}>
          {children}
        </H2>
      )
    case 'h2Extend':
      return (
        <H2Extended as="h2" {...rest}>
          {children}
        </H2Extended>
      )
    case 'h3':
      return (
        <H3 as="h3" {...rest}>
          {children}
        </H3>
      )
    case 'h3Extend':
      return (
        <H3Extended as="h3" {...rest}>
          {children}
        </H3Extended>
      )
    case 'h4':
      return (
        <H4 as="h4" {...rest}>
          {children}
        </H4>
      )
    case 'h5':
      return (
        <H5 as="h5" {...rest}>
          {children}
        </H5>
      )
    case 'h6':
      return (
        <H6 as="h6" {...rest}>
          {children}
        </H6>
      )
    case 'p':
      return (
        <PARA as="p" {...rest}>
          {children}
        </PARA>
      )
    case 'pExtend':
      return (
        <PARAExtended as="p" {...rest}>
          {children}
        </PARAExtended>
      )
    case 'a':
      return (
        <ANCHOR as="a" {...rest}>
          {children}
        </ANCHOR>
      )
    case 'aExtend':
      return (
        <ANCHORExtended as="a" {...rest}>
          {children}
        </ANCHORExtended>
      )
    case 'label':
      return (
        <LABEL as="label" {...rest}>
          {children}
        </LABEL>
      )
    case 'tooltip':
      return (
        <TOOLTIP as="span" {...rest}>
          {children}
        </TOOLTIP>
      )
    case 'helperText':
      return (
        <HELPERTEXT as="p" {...rest}>
          {children}
        </HELPERTEXT>
      )
    case 'span':
      return (
        <HELPERTEXT as="span" {...rest}>
          {children}
        </HELPERTEXT>
      )
    default:
      return <TextDefault {...rest}>{children}</TextDefault>
  }
}

// Main Text component
export const Text = ({
  as,
  children,
  icon,
  styleBox,
  ...rest
}: TextComponentProps) => {
  const iconStyle = css({
    ...(icon?.Before && { marginRight: '$space$8' }),
    ...(icon?.After && { marginLeft: '$space$8' }),
  })

  const textElements = generateTextElements(as, children, rest)

  return (
    <>
      {icon ? (
        <Flex css={styleBox} align={'center'}>
          {icon?.Before && (
            <SvgWrap
              size={icon?.size}
              className={iconStyle.toString()}
              css={icon?.gap}
              color={rest.color}
            >
              {icon.Before}
            </SvgWrap>
          )}
          {textElements}
          {icon?.After && (
            <SvgWrap
              className={iconStyle.toString()}
              css={icon?.gap}
              color={rest.color}
            >
              {icon.After}
            </SvgWrap>
          )}
        </Flex>
      ) : (
        textElements
      )}
    </>
  )
}
